import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Assignment,
  AttachMoney,
  Schedule,
  CheckCircle,
  SmartToy,
} from '@mui/icons-material';

import { apiService } from '../services/api';
import { DashboardMetrics } from '../types';
import OverdueFlow from './OverdueFlow';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

function MetricCard({ title, value, icon, color, subtitle }: MetricCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 1,
              mr: 2,
              color: 'white',
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load dashboard metrics
      try {
        const metricsData = await apiService.getDashboardMetrics();
        // Ensure agent_performance is always an array
        if (metricsData && typeof metricsData === 'object') {
          metricsData.agent_performance = Array.isArray(metricsData.agent_performance) 
            ? metricsData.agent_performance 
            : [];
        }
        setMetrics(metricsData);
      } catch (err) {
        console.error('Dashboard metrics failed:', err);
        setError('Failed to load dashboard metrics');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
      // Set fallback metrics even on error to prevent crashes
      setMetrics({
        total_cases: 0,
        active_cases: 0,
        resolved_cases: 0,
        total_amount: 0,
        average_days_overdue: 0,
        success_rate: 0,
        agent_performance: [],
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Main Feature: Overdue Flow */}
      <OverdueFlow />
      
      {/* Quick Metrics Summary */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          System Overview
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Cases"
              value={metrics?.total_cases || 0}
              icon={<Assignment />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Cases"
              value={metrics?.active_cases || 0}
              icon={<Schedule />}
              color="#ff9800"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Success Rate"
              value={`${metrics?.success_rate || 0}%`}
              icon={<CheckCircle />}
              color="#4caf50"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Amount"
              value={`$${((metrics?.total_amount || 0) / 1000000).toFixed(1)}M`}
              icon={<AttachMoney />}
              color="#9c27b0"
            />
          </Grid>
        </Grid>

        {/* Agent Health Status */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            🤖 Agent Health Status
          </Typography>
          <Grid container spacing={2}>
            {(metrics?.agent_performance && Array.isArray(metrics.agent_performance) ? metrics.agent_performance : []).length > 0 ? (
              (metrics?.agent_performance && Array.isArray(metrics.agent_performance) ? metrics.agent_performance : []).map((agent) => (
                <Grid item xs={12} sm={6} md={4} key={agent.agent_name}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SmartToy color={agent.status === 'healthy' ? 'success' : 'error'} />
                    <Typography variant="body2" fontWeight="medium">
                      {agent.agent_name}
                    </Typography>
                    <Chip 
                      size="small" 
                      label={`${agent.success_rate.toFixed(1)}%`}
                      color={agent.success_rate > 95 ? 'success' : agent.success_rate > 90 ? 'warning' : 'error'}
                    />
                  </Box>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" align="center">
                  No agent performance data available. Agents will appear here once they start processing cases.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}